var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useState, useMemo, useEffect } from 'react';
import { translate } from '@utils/i18n';
import { saveCacheToLocalStorage, loadCacheFromLocalStorage } from '../../../helpers/localStorage';
import { isCompatibleParentType } from '../utils/filteredParentVisibility';
// Default filters
const DEFAULT_FILTERS = {
    search: '',
    typeIds: [],
    activeState: 'active'
};
const useFilteredComponents = ({ components, componentTypes = [], buildingId, attachableId, initialTypeIds = [] }) => {
    // Create cache key based on buildingId or attachableId
    const CACHE_KEY = useMemo(() => {
        if (buildingId) {
            return `kendo/components/index/building-${buildingId}`;
        }
        if (attachableId) {
            return `kendo/components/index/attachable-${attachableId}`;
        }
        return 'kendo/components/index/default';
    }, [buildingId, attachableId]);
    // Load initial filters: URL-seeded type IDs win over localStorage so that
    // clicking a type breakdown link pre-selects the filter on entry.
    // After the user interacts with filters, localStorage takes over normally.
    const getInitialFilters = () => {
        if (initialTypeIds.length > 0) {
            return Object.assign(Object.assign({}, DEFAULT_FILTERS), { typeIds: initialTypeIds });
        }
        const cache = loadCacheFromLocalStorage(CACHE_KEY);
        if (cache && cache.filters) {
            return cache.filters;
        }
        return DEFAULT_FILTERS;
    };
    // Filters state with initial value from localStorage (or URL seed)
    const [filters, setFilters] = useState(getInitialFilters);
    // Save filters to localStorage whenever they change (following Buildings pattern)
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
        const { search } = filters, filtersToSave = __rest(filters, ["search"]);
        saveCacheToLocalStorage(CACHE_KEY, { filters: filtersToSave });
    }, [filters, CACHE_KEY]);
    // Reset cache functionality (following Buildings pattern)
    const onResetCache = () => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(translate('are_you_sure', { namespace: 'generic' })))
            return;
        localStorage.removeItem(CACHE_KEY);
        window.location.reload();
    };
    // Filter components based on current filters (extracted from ComponentsTab)
    const componentTypesById = useMemo(() => {
        return new Map(componentTypes.map((componentType) => [componentType.id, componentType]));
    }, [componentTypes]);
    const filteredComponents = useMemo(() => {
        if (!components.length)
            return [];
        const filterComponent = (component) => {
            // Active state filter
            if (filters.activeState === 'active' && !component.active) {
                return false;
            }
            if (filters.activeState === 'inactive' && component.active) {
                return false;
            }
            // If activeState is 'all', show all components regardless of active status
            // Search filter
            if (filters.search) {
                const searchTerm = filters.search.toLowerCase();
                const matchesSearch = component.name.toLowerCase().includes(searchTerm) ||
                    (component.location && component.location.toLowerCase().includes(searchTerm)) ||
                    (component.barcode && component.barcode.toLowerCase().includes(searchTerm));
                if (!matchesSearch)
                    return false;
            }
            // Type filter. A compatible parent type stays visible even with zero matching children
            // so a filtered component can be dragged onto it (IP-16257).
            if (filters.typeIds.length > 0 && !filters.typeIds.includes(component.typeId)) {
                return isCompatibleParentType(componentTypesById.get(component.typeId), filters.typeIds);
            }
            return true;
        };
        const filterComponentsRecursive = (componentsList) => {
            return componentsList.reduce((filtered, component) => {
                const matchesFilters = filterComponent(component);
                const filteredChildren = component.children ? filterComponentsRecursive(component.children) : [];
                // Include component if it matches filters OR has matching children
                if (matchesFilters || filteredChildren.length > 0) {
                    filtered.push(Object.assign(Object.assign({}, component), { children: filteredChildren }));
                }
                return filtered;
            }, []);
        };
        return filterComponentsRecursive(components);
    }, [components, filters, componentTypesById]);
    return {
        filters,
        setFilters,
        filteredComponents,
        onResetCache,
        CACHE_KEY
    };
};
export default useFilteredComponents;
